import React, {Fragment, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import window from "../../../lib/WindowExtended";
import {getShopId} from "../../../lib/getShopId";
import AmazonPayAddressWidget from "../../../lib/payments/AmazonPayAddressWidget";
import {captureSentryException} from "../../../../helpers/sentryHelper";
import {CheckoutError, DEFAULT_ERROR_MESSAGE, scrollToTop, StringWithAnchorComp} from "../../Checkout";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {
  AddressResponseData,
  CountriesFactory,
  IAddressData,
  IAddressResponseData,
  ICountriesLookup,
} from "@connox/typescript-library";
import AddressForm, {IAddressFormData} from "./AddressForm";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import Spinner from "../../../utils/loader/Spinner";
import {ICartRequestHandlerPutCartOptions} from "@connox/typescript-library/types/cart/requestHandlers/CartRequestHandler";
import * as Sentry from "@sentry/browser";
import {Payments} from "../../../lib/payments/PaymentController";
import styles from "./AddressStep.module.scss";
import {useCustomerContext} from "../../../customer/CustomerContextProvider/CustomerContextProvider";
import isSameObject from "../../../../helpers/isSameObject";

type IAddressesState = {
  amazonPayAddress?: IAddressData;
  isInvoiceSameAsDelivery: boolean;
  deliveryAddress: IAddressFormData;
  invoiceAddress: IAddressFormData;
  isInvAddressValid?: boolean;
  isDelAddressValid?: boolean;
};

// eslint-disable-next-line complexity
const AddressStep: React.FC<{isVisible: boolean}> = ({isVisible}) => {
  const {t} = useTranslation("checkout");
  const {checkout} = useCheckoutContext();
  const {customer} = useCustomerContext();
  const {countryCode} = checkout.cartData;
  const {viewState, setViewState, cart} = useCartContext();
  const [countries, setCountries] = useState<ICountriesLookup[]>([]);
  const [deliveryCountries, setDeliveryCountries] = useState<ICountriesLookup[]>([]);
  // will set to true when we are waiting for whatever request to come back ( as in amazon pay widget)
  const [isLoading, setIsLoading] = useState(!!window.orderReferenceId);
  // save possible errors or bad messages in checkout/cart/positions after every api call
  const [errors, setErrors] = useState<(string | JSX.Element)[]>([]);
  // save possible phone unexpted phone number error
  const [phoneError, setPhoneError] = useState<string>("");
  // save the country code that is not allowed for shipping( this error comes in cart in positions level)
  const [shippingErrorCountryCode, setShippingErrorCountryCode] = useState<string>("");
  // this state will trigger the form errors to avoid submitting address in case the form is invalid
  const [trigger, setTrigger] = useState<boolean>();
  const [defaultInvAddress, setDefInvAddess] = useState<IAddressFormData>();
  const [defaultDelAddress, setDefDelAddess] = useState<IAddressFormData>();

  // main state for all addresses
  const invAdd = (checkout.invoiceAddress ?? {...emptyAddress, countryCode}) as IAddressFormData;
  const delAdd = (
    checkout.deliveryAddress ? {...checkout.deliveryAddress, countryCode} : {...invAdd, countryCode}
  ) as IAddressFormData;

  const initialState: IAddressesState = {
    isInvoiceSameAsDelivery: checkout.deliveryAddress ? isSameAddress(invAdd, delAdd) : true,
    invoiceAddress: invAdd,
    deliveryAddress: delAdd,
    isInvAddressValid: true,
    isDelAddressValid: true,
  };

  const [addressesState, setAddressesState] = useState<IAddressesState>(initialState);

  // effect to get all countries when the componenets get mounted, here will initiaized some states variables
  useEffect(() => {
    void CountriesFactory.getAllCountries(getShopId()).then((data) => {
      setCountries(data);
      setDeliveryCountries(data.filter((country) => country.isDeliveryAllowed));
      // in case the invoice country cant be deliverd expand delivery address form
      const invoiceCountryCode = addressesState.invoiceAddress.countryCode;
      const isInvoiceCountryAllowedForDelvivery = data.find(
        (country) => country.isDeliveryAllowed && country.countryCode === invoiceCountryCode,
      );
      setShippingErrorCountryCode(isInvoiceCountryAllowedForDelvivery ? "" : invoiceCountryCode);
      if (!isInvoiceCountryAllowedForDelvivery) {
        setAddressesState({...addressesState, isInvoiceSameAsDelivery: false});
      }
    });
  }, []);

  // set last addresses just in case invoice address is null
  useEffect(() => {
    if ((customer.isLoggedIn || customer.isRecognised) && !checkout.invoiceAddress) {
      void customer.getLastAddresses(getShopId()).then((addresses) => {
        const invoiceAddress = addresses.find((address) => address.isLastInvoiceAddress) as IAddressFormData;
        const deliveryAddress = addresses.find((address) => address.isLastDeliveryAddress) as IAddressFormData;
        const isSameAddresses = isSameAddress(invoiceAddress, deliveryAddress);

        setAddressesState({
          ...addressesState,
          invoiceAddress: isSameAddresses ? {...invoiceAddress, countryCode} : invoiceAddress,
          deliveryAddress: {...deliveryAddress, countryCode},
          isInvoiceSameAsDelivery: isSameAddresses,
        });

        setDefDelAddess(deliveryAddress);
        setDefInvAddess(invoiceAddress);
      });
    }
  }, [customer.isLoggedIn]);

  // recalculate the shipping cost after every change to the delivery country under some conditions
  useEffect(() => {
    if (
      !window.orderReferenceId &&
      !addressesState.isInvoiceSameAsDelivery &&
      addressesState.deliveryAddress?.countryCode &&
      addressesState.deliveryAddress?.countryCode !== countryCode &&
      addressesState.deliveryAddress?.countryCode !== shippingErrorCountryCode &&
      isVisible
    ) {
      void submitAddress(true);
    }
  }, [addressesState.deliveryAddress?.countryCode]);

  // make a call to recalculate the shipping cost after every change to the invoice  country under some conditions
  // make a call when the isInvoiceSameAsDelivery state changes.
  useEffect(() => {
    if (countries.length && addressesState.invoiceAddress) {
      const invoiceCountryCode = addressesState.invoiceAddress?.countryCode;
      const isInvoiceCountryAllowedForDelivery = countries.find(
        (country) =>
          country.isDeliveryAllowed &&
          country.countryCode === invoiceCountryCode &&
          invoiceCountryCode !== shippingErrorCountryCode,
      );
      setShippingErrorCountryCode(isInvoiceCountryAllowedForDelivery ? "" : invoiceCountryCode);
      if (!isInvoiceCountryAllowedForDelivery) {
        setAddressesState({...addressesState, isInvoiceSameAsDelivery: false});
      } else {
        if (
          addressesState.isInvoiceSameAsDelivery &&
          isVisible &&
          !window.orderReferenceId &&
          addressesState.invoiceAddress?.countryCode !== countryCode
        ) {
          void submitAddress(true);
        }
      }
    }
  }, [addressesState.invoiceAddress?.countryCode, addressesState.isInvoiceSameAsDelivery]);

  // more  filtering for  delivery countries.
  useEffect(() => {
    setDeliveryCountries(deliveryCountries.filter((country) => country.countryCode !== shippingErrorCountryCode));
  }, [shippingErrorCountryCode]);

  // this effect will handle possible errors after every change to apay address or changing in countries
  useEffect(() => {
    const {amazonPayAddress} = addressesState;
    if (deliveryCountries.length && amazonPayAddress) {
      if (!deliveryCountries.some((country) => country.countryCode === amazonPayAddress.countryCode)) {
        setErrors([t("Das ausgewählte Lieferland kann leider nicht beliefert werden")]);

        return;
      }
      if (isToNorthIreland(amazonPayAddress.countryCode, amazonPayAddress.zip)) {
        setErrors(errors.concat([t("Die ausgewählte Adresse kann leider nicht beliefert werden")]));

        return;
      }
      window.isBillingAgreementAddressSet = true;
      window.isBillingAgreementPaymentSet = false;
      window.isPreauthorizingDone = false;
      setIsLoading(false);
      setErrors([]);
    }
  }, [deliveryCountries, addressesState.amazonPayAddress]);

  // make a call to recalculate the shipping cost after every change to the apay address  country under some conditions
  useEffect(() => {
    if (
      addressesState.amazonPayAddress &&
      addressesState.amazonPayAddress.countryCode !== checkout.cartData.countryCode
    ) {
      void submitAddress(true);
    }
  }, [addressesState.amazonPayAddress?.countryCode]);

  // this effect will check possible cart shipping error and save this in the state
  useEffect(() => {
    const {positions} = checkout.cartData;
    positions.forEach((pos) => {
      if (pos.messages) {
        pos.messages.forEach((msg) => {
          if (msg.code === "NOT_ALLOWED_FOR_COUNTRY" || msg.code === "SHIPPING_NOT_FOUND") {
            setShippingErrorCountryCode(checkout.cartData?.countryCode);
            setAddressesState({...addressesState, isInvoiceSameAsDelivery: false});
            if (addressesState.amazonPayAddress) {
              setErrors([t("Das ausgewählte Lieferland kann leider nicht beliefert werden")]);
            }
          }
        });
      } else {
        setShippingErrorCountryCode("");
      }
    });
  }, [checkout.cartData]);

  // reset amazon address when amazon pay is canceled
  useEffect(() => {
    if (!window.orderReferenceId) {
      delete addressesState.amazonPayAddress;
      setAddressesState(addressesState);
    }
  }, [window.orderReferenceId]);

  // update the main state after every apay address change
  const onAmazonAddress = (aPayAddress: IAddressData) => {
    setIsLoading(false);
    setAddressesState((prevState) => ({
      ...prevState,
      amazonPayAddress: aPayAddress,
    }));
  };

  const onAmazonError = (errorString: string) => {
    setErrors(() => {
      if (
        errorString ===
        "[AmazonPayAddress] AddressNotModifiable: Lieferadresse kann im Status OPEN nicht geändert werden"
      ) {
        return [<StringWithAnchorComp key={"[AmazonPayAddress]"} message={errorString} />];
      } else {
        const sentryEventId = captureSentryException(new Error(errorString), checkout, "Address step amazon error");

        return [t(errorString, t(DEFAULT_ERROR_MESSAGE, {sentryEventId}))];
      }
    });
  };

  const invoiceAddr = () =>
    addressesState.amazonPayAddress
      ? addressesState.amazonPayAddress
      : convertToAddressData(addressesState.invoiceAddress);

  const deliveryAddr = () =>
    addressesState.isInvoiceSameAsDelivery || addressesState.amazonPayAddress
      ? invoiceAddr()
      : convertToAddressData(addressesState.deliveryAddress);

  const isAddressesValid = (): boolean => {
    if (!window.isIntoChangingCountry) {
      return addressesState.isInvoiceSameAsDelivery
        ? !!addressesState.isInvAddressValid
        : !!addressesState.isInvAddressValid && !!addressesState.isDelAddressValid;
    }

    return true;
  };

  const isCorporateCustomerOptionsNeeded = (): boolean =>
    !addressesState.amazonPayAddress &&
    checkout.cartData.isCorporateCustomer &&
    addressesState.invoiceAddress?.vatNumber !== checkout.cartData.vatNumber;

  // this function will submit the addresses state
  const submitAddress = async (isIntoChangingCountry?: boolean) => {
    window.isIntoChangingCountry = isIntoChangingCountry || false;

    if (!isAddressesValid()) {
      setTrigger(true);

      return;
    }
    setTrigger(false);

    if (!addressesState.invoiceAddress && !addressesState.amazonPayAddress) {
      return;
    }

    setIsLoading(true);
    const invoiceAddress = invoiceAddr();

    const deliveryAddress = deliveryAddr();

    if (isToNorthIreland(deliveryAddress.countryCode, deliveryAddress.zip)) {
      setErrors([t("Die ausgewählte Adresse kann leider nicht beliefert werden")]);
      setIsLoading(false);

      return;
    }

    try {
      let cartOptions: ICartRequestHandlerPutCartOptions = {};
      const {isNet} = checkout.cartData;

      if (isCorporateCustomerOptionsNeeded()) {
        cartOptions = {vatNumber: addressesState.invoiceAddress?.vatNumber ?? ""};
      }
      /*
       * If customer is not just into changing country (extra request necessary
       * for shipping cost calculation in the cart detaisl) but into continuing
       * and only if country really changes ('useEffect' side effects)
       */
      if (deliveryAddress.countryCode && deliveryAddress.countryCode !== checkout.cartData.countryCode) {
        cartOptions = {...cartOptions, countryCode: deliveryAddress.countryCode};
      }
      if (Object.keys(cartOptions).length) {
        await cart.put(cartOptions);
      }
      await checkout.put({
        address: {
          delivery: deliveryAddress,
          invoice: invoiceAddress,
        },
        // has to be set again to apay, when country is reset in cart on apay user
        paymentId:
          window.orderReferenceId && deliveryAddress.countryCode !== checkout.cartData.countryCode
            ? Payments.AmazonPay
            : checkout.paymentId ?? undefined,
      });

      setIsLoading(false);
      if (!isIntoChangingCountry) {
        scrollToTop();
      }

      if (isNet !== checkout.cartData.isNet) {
        await cart.put({});
      }
      setPhoneError("");
      setViewState({isRegistering: viewState.isRegistering, partials: undefined});
    } catch (err) {
      /* eslint-disable */
      const errMessage: string =
        err?.originalError?.response?.data.result.code || err?.originalError?.message || err.message;
      /* eslint-enable */
      const sentryEventId = Sentry.captureException(err);
      setIsLoading(false);
      if (errMessage.includes("PHONE")) {
        setPhoneError("");
        setPhoneError(errMessage);
      } else {
        setErrors([t(errMessage, t(DEFAULT_ERROR_MESSAGE, {sentryEventId}))]);
      }
    }
  };

  if (!isVisible) {
    return null;
  }

  if (window.orderReferenceId) {
    return (
      <div className={`${styles.wrapper} Mobile-Only-Friendly`}>
        <CheckoutError>{errors}</CheckoutError>
        <h1>{t("Ihre Lieferadresse")}</h1>
        {getShopId() === 100001 && (
          <AmazonPayAddressWidget
            availableCountries={deliveryCountries}
            onError={onAmazonError}
            onAddress={onAmazonAddress}
          />
        )}
        <CartDetails reduced={true} />
        <Button
          action={(evt) => {
            evt.preventDefault();
            void submitAddress();
          }}
          styles={[ButtonStyles.wide]}
          type={isLoading || errors.length ? ButtonType.disabled : ButtonType.default}
        >
          {isLoading ? <Spinner /> : <span>{t("Weiter")}</span>}
        </Button>
      </div>
    );
  }

  return (
    <div className={`${styles.wrapper} Mobile-Only-Friendly`}>
      <CheckoutError>{errors}</CheckoutError>
      <h1>{t("Ihre Rechnungsadresse")}</h1>
      {countries.length && (
        <AddressForm
          onChange={(address) => {
            setAddressesState((prevState) => ({...prevState, invoiceAddress: address}));
          }}
          onValid={(isValid) => {
            setAddressesState({...addressesState, isInvAddressValid: isValid});
          }}
          triggerErrors={trigger}
          phoneError={phoneError}
          countries={countries}
          lastAddress={defaultInvAddress || (emptyAddress as IAddressFormData)}
        />
      )}

      <div className={styles.checkboxIsSameAddress}>
        <input
          type={"checkbox"}
          id={"isSameAddressCheckBox"}
          checked={addressesState.isInvoiceSameAsDelivery && !shippingErrorCountryCode}
          disabled={!!shippingErrorCountryCode}
          onChange={() => {
            setAddressesState((prevState) => ({
              ...prevState,
              isInvoiceSameAsDelivery: !prevState?.isInvoiceSameAsDelivery,
            }));
          }}
        />
        <label htmlFor={"isSameAddressCheckBox"}>{t("Die Lieferadresse entspricht der Rechnungsadresse")}</label>
      </div>

      {(!addressesState.isInvoiceSameAsDelivery || !!shippingErrorCountryCode) && (
        <Fragment>
          {!!shippingErrorCountryCode && (
            <p>
              {t("Rechnungsadresse Land ist nicht lieferbar Hinweis...", {
                country: countries.find((country) => country.countryCode === shippingErrorCountryCode)?.name || "",
                addressType:
                  shippingErrorCountryCode === addressesState.invoiceAddress?.countryCode
                    ? t("Rechnungsadresse")
                    : t("Lieferadresse"),
              })}
              {["DK", "GB", "CH", "DE", "FR"].includes(shippingErrorCountryCode) && (
                <React.Fragment>
                  <span>{" " + t("oder besuchen Sie unseren lokalen Shop unter")}</span>
                  <a href={"https://" + getShopUrl[shippingErrorCountryCode]}>
                    {" " + getShopUrl[shippingErrorCountryCode]}
                  </a>
                </React.Fragment>
              )}
              {"."}
            </p>
          )}
          <h1>{t("Ihre Lieferadresse")}</h1>
          {deliveryCountries.length && (
            <AddressForm
              isDelivery={true}
              onChange={(address) => {
                setAddressesState((prevState) => ({...prevState, deliveryAddress: address}));
              }}
              onValid={(isValid) => {
                setAddressesState({...addressesState, isDelAddressValid: isValid});
              }}
              triggerErrors={trigger}
              phoneError={phoneError}
              countries={deliveryCountries}
              lastAddress={defaultDelAddress || (emptyAddress as IAddressFormData)}
            />
          )}
        </Fragment>
      )}
      <p style={{fontSize: ".875rem"}}>{t("* Pflichtfelder")}</p>
      <CartDetails reduced={true} />
      <Button
        action={(evt) => {
          evt.preventDefault();
          void submitAddress();
        }}
        styles={[ButtonStyles.wide]}
        type={isLoading ? ButtonType.disabled : ButtonType.default}
      >
        {isLoading ? <Spinner /> : <span>{t("Weiter")}</span>}
      </Button>
    </div>
  );
};

export const isToNorthIreland = (countryCode: string, zip: string): boolean => {
  return countryCode === "GB" && zip.toUpperCase().startsWith("BT");
};

export const isSameAddress = (invoice: AddressResponseData | null, delivery: AddressResponseData | null): boolean => {
  const keysToBeIgnored = ["addressId", "messages", "isLastInvoiceAddress", "isLastDeliveryAddress", "rawData"];

  return isSameObject<AddressResponseData>(invoice, delivery, keysToBeIgnored);
};

export const getShopUrl: {[countryCode: string]: string} = {
  CH: getShopId() === 100008 ? "fr.connox.ch" : "www.connox.ch",
  DK: "www.connox.dk",
  GB: "www.connox.co.uk",
  DE: "www.connox.de",
  FR: "www.connox.fr",
};

export const convertToAddressData = (data: Partial<IAddressFormData>): IAddressData => {
  return {
    email: data.email || "",
    firstName: data.firstName || "",
    gender: data.gender as "M" | "F",
    lastName: data.lastName || "",
    city: data.city || "",
    company: data.company || "",
    countryCode: data.countryCode || "",
    dateOfBirth: data.dateOfBirth || null,
    department: data.department || "",
    number: data.number || "",
    phone: data.phone || "",
    state: data.state || "",
    street: data.street || "",
    title: data.title || "",
    zip: data.zip || "",
  };
};

export const emptyAddress: IAddressResponseData = {
  addressId: "",
  addressSupplement: null,
  city: "",
  company: "",
  countryCode: "",
  dateOfBirth: "",
  department: "",
  email: "",
  firstName: "",
  gender: "" as "M" | "F",
  isLastDeliveryAddress: false,
  isLastInvoiceAddress: false,
  lastName: "",
  number: "",
  phone: "",
  state: "",
  street: "",
  title: "",
  zip: "",
};

export default AddressStep;
